"use client";

import React from "react";
import NavbarLogo from "@/components/layout/header/navbar-logo";
import dynamic from "next/dynamic";
import { cn } from "@/lib/utils";
import { useViewMode } from "@/context/ViewModeContext";
import { useCurrentDeviceContext } from "@/context/DeviceContext";
import NavbarSearchButton from "@/components/layout/header/links/navbar-search-button";
import NavbarLinks from "@/components/layout/header/links/navbar-links";
import SidebarAddVenueButton from "@/components/views/modals/sidebar/SidebarAddVenue";
import NavbarSidebarAction from "@/components/layout/header/navbar-sidebar-action";
import MapPinIcon from "@heroicons/react/24/outline/MapPinIcon";
import Bars3Icon from "@heroicons/react/24/outline/Bars3Icon";
import { useTranslation } from "@/hooks/translationContext";
import { useVenueListPage } from "@/hooks/listPageContext";
import { useSite } from "@/lib/hooks/site";
import { useRouter, usePathname } from "next/navigation";

interface HeaderContentProps {
  site: Site.Site;
  isMobile: boolean;
}

export default function HeaderContent({ site, isMobile }: HeaderContentProps) {
  const { viewMode, toggleViewMode, setViewMode } = useViewMode();
  const { t } = useTranslation();
  const { isVenueListPage } = useVenueListPage();
  const siteData = useSite();
  const router = useRouter();
  const isHomepage = usePathname() === "/";

  const isMapView = viewMode === "map";

  const handleMapToggle = () => {
    if (isVenueListPage) {
      // Already on list page, just toggle view mode
      toggleViewMode();
    } else {
      // Not on list page, navigate to list page first, then set map view
      const generalListRoute = siteData?.setting?.routes?.generalList;
      if (generalListRoute) {
        router.push(`/${generalListRoute}`);
        // Set view mode after navigation starts
        setTimeout(() => {
          setViewMode("map");
        }, 0);
      }
    }
  };

  return (
    <div
      className={cn(
        "max-md:pt-4 flex justify-between items-center w-full md:w-auto",
        viewMode == "map" ? "md:flex hidden" : "",
        // Header frame on every page. The negative margin pushes the border outward
        // (a bit past the grid) while the matching padding keeps the logo and
        // right-side actions at their original grid positions. Faint border per
        // background: light over the dark homepage header, pale gray over the
        // white header on other pages. On the homepage the frame gets a solid
        // dark fill so the hero image shows only outside the frame, not inside it.
        "md:-mx-6 md:px-6 md:py-[14px] md:rounded-2xl md:border",
        isHomepage ? "md:border-white/15 md:bg-primary" : "md:border-gray-200",
      )}
    >
      <NavbarLogo />

      {/* Mobile gets its own search row below the header (see layout/header),
          so it is rendered here for desktop only instead of twice. */}
      {!isMobile && (
        <div className="flex">
          <NavbarSearchButton />
        </div>
      )}

      <div className="flex items-center gap-2 max-md:py-2">
        <div className="hidden md:flex">
          <NavbarLinks site={site} />
        </div>

        {/* Harita/Listeleme Toggle Button - Desktop Only */}
        <button
          onClick={handleMapToggle}
          className="hidden md:flex items-center gap-1.5 px-4 h-[42px] bg-[#f39b13] rounded-full"
        >
          {isMapView ? (
            <Bars3Icon className="w-4 h-4 text-white" />
          ) : (
            <MapPinIcon className="w-4 h-4 text-white" />
          )}
          <span
            className="text-sm font-bold text-white"
            style={{ fontFamily: "'Inter', sans-serif" }}
          >
            {isMapView
              ? t("list-button", { defaultValue: "Listeleme" })
              : t("map-button", { defaultValue: "Harita" })}
          </span>
        </button>

        <div className="flex">
          <SidebarAddVenueButton customClasses="max-md:!h-[32px] max-md:!px-2 md:!h-[42px] !rounded-full" />
        </div>

        <NavbarSidebarAction />
      </div>
    </div>
  );
}